import { Fragment, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUp, BookOpen } from "lucide-react";
import { CheckoutButton } from "./CheckoutButton";
import { TikTokIcon, LinkedInIcon } from "./Scott";
import { useSite, useImages } from "@/context/SiteContent";
import { useActiveSection } from "@/hooks/useSiteEffects";
import { scrollToEl, scrollToId } from "@/lib/scroll";

const links = [["fiveps", "The 5Ps"], ["ebook", "What's Inside"], ["quiz", "Quiz"], ["library", "52 Lessons"], ["scott", "About"]];
const linkIds = links.map(([id]) => id);

const MobileMenu = ({ open, onClose }) => {
  const { price, bookingCta, links: social } = useSite();
  const images = useImages();
  useEffect(() => {
    if (!open) return undefined;
    window.__lenis?.stop();
    return () => window.__lenis?.start();
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="mnav" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} data-testid="mobile-menu">
          <motion.div className="mnav-panel" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}>
            <div className="mnav-head">
              <img alt="TransitionWorks" src={images.logoDark} />
              <button className="mnav-close" onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close"><X size={20} /></button>
            </div>
            <nav className="mnav-links">
              {links.map(([id, label], i) => (
                <motion.a key={id} href={`#${id}`} onClick={onClose} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.05 }} data-testid={`mobile-menu-link-${id}`}>
                  <small>0{i + 1}</small>{label}
                </motion.a>
              ))}
              <motion.a href="#booking" onClick={onClose} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.36 }} data-testid="mobile-menu-link-booking"><small>06</small>Book Scott</motion.a>
            </nav>
            <div className="mnav-actions">
              <CheckoutButton className="btn btn-gold btn-block" testId="mobile-menu-checkout-btn">Get the eBook • {price}</CheckoutButton>
              <a className="btn btn-outline btn-block" href="#booking" onClick={onClose} data-testid="mobile-menu-booking-btn">{bookingCta}</a>
              <Link className="mnav-read" to="/read" onClick={onClose} data-testid="mobile-menu-read-link"><BookOpen size={15} /> Read your eBook</Link>
            </div>
            <div className="mnav-social">
              <a href={social.tiktok} target="_blank" rel="noopener noreferrer"><TikTokIcon /> TikTok</a>
              <a href={social.linkedin} target="_blank" rel="noopener noreferrer"><LinkedInIcon /> LinkedIn</a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Nav = ({ scrolled, progress }) => {
  const { price, bookingCta } = useSite();
  const images = useImages();
  const active = useActiveSection(linkIds);
  const [menu, setMenu] = useState(false);
  return (
  <>
    <div className="progress" style={{ width: `${progress}%` }} data-testid="scroll-progress" />
    <nav className={`nav${scrolled ? " scrolled" : ""}`} data-testid="site-nav">
      <div className="container nav-inner">
        <a href="#top" data-testid="nav-logo-link"><img alt="TransitionWorks" className="nav-logo" src={images.logoDark} /></a>
        <div className="nav-links">
          {links.map(([id, label], i) => (
            <Fragment key={id}>
              {i > 0 && <span className="nav-dot" />}
              <a href={`#${id}`} className={active === id ? "is-active" : ""} data-testid={`nav-link-${id}`}>{label}</a>
            </Fragment>
          ))}
          <Link className="nav-read-link" to="/read" data-testid="nav-read-link"><BookOpen size={14} /> Read your eBook</Link>
          <a className="btn btn-outline nav-book-btn" href="#booking" data-testid="nav-booking-btn">{bookingCta}</a>
          <CheckoutButton testId="nav-checkout-btn">Get the eBook • {price}</CheckoutButton>
        </div>
        <button className="nav-burger" onClick={() => setMenu(true)} aria-label="Open menu" data-testid="nav-menu-btn"><Menu size={22} /></button>
      </div>
    </nav>
    <MobileMenu open={menu} onClose={() => setMenu(false)} />
  </>
  );
};

export const JourneyRail = ({ index, sections }) => (
  <div className="journey-rail" data-testid="journey-rail">
    {sections.map((s, i) => (
      <button key={s.label} className={`rail-dot${i === index ? " active" : ""}`} data-label={s.label} aria-label={`Go to ${s.label}`} onClick={() => scrollToEl(s.el)} data-testid={`rail-dot-${i}`} />
    ))}
  </div>
);

export const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const f = () => setShow(window.scrollY > 900);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button className="back-top" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 14 }} onClick={() => scrollToId("top")} aria-label="Back to top" data-testid="back-to-top">
          <ArrowUp size={18} />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export const MobileBar = ({ stickyText }) => {
  const { price } = useSite();
  const images = useImages();
  return (
  <div className="mobilebar" data-testid="mobile-bar">
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <img src={images.icon} alt="" style={{ width: 42, height: 42, borderRadius: 11 }} />
      <div style={{ flex: 1, color: "white" }}>
        <strong style={{ display: "block" }} data-testid="mobile-bar-title">{stickyText}</strong>
        <span style={{ fontSize: ".76rem", color: "rgba(255,255,255,.55)" }}>{price} • One-time</span>
      </div>
      <CheckoutButton testId="mobile-checkout-btn" style={{ minHeight: 44, padding: "0 13px" }}>Get eBook</CheckoutButton>
    </div>
  </div>
  );
};
